/**
 * The Versions panel — Resource Plan Epic 8 (RP-8.1 … RP-8.4), M4.
 *
 * Three things on one panel, because they are one question: what has this plan been, how
 * does now differ from then, and can I start again from the shape that was agreed.
 *
 *   • the frozen versions, newest first, with the figures they froze (RP-8.1)
 *   • a compare, of any two of them or of one against the live plan (RP-8.3, RP-7.4)
 *   • clone, restore, and the Change Order shortcut (RP-8.2, RP-8.4)
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * RESTORE CONFIRMS BY NAME, BECAUSE IT REPLACES THE GRID
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-8.2's "or new version" puts a frozen version's content back into the plan, which
 * discards whatever is in the grid now. The confirm names the version being restored AND
 * the line count being replaced, so a planner cannot mistake it for the "clone into a new
 * plan" button sitting next to it — the two differ by one radio button and one of them is
 * destructive.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * IN EVERY STATUS: NEW VERSION, RESTORE, DELETE YOUR OWN
 * ──────────────────────────────────────────────────────────────────────────────
 * Planners save versions by hand ("New version") in any status but Locked, alongside the
 * Sent / Approved / Won snapshots the lifecycle takes. One row is marked Live — the version
 * the plan matches. Restoring makes another row live without adding one, and throws away
 * unsaved edits, which its confirm says; with unsaved edits the live row can be restored
 * too, which is how a planner discards them. A planner may delete a version they saved
 * themselves, never the live one and never the Sent / Approved / Won record.
 */

import { ArrowRight, Copy, GitCompare, History, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { CommitVersionButton } from "@/components/plans/CommitVersionButton";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { MonthSelect } from "@/components/shared/MonthSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useChangeOrderCompareRefs,
  useClonePlan,
  useDeletePlanVersion,
  usePlanComparison,
  usePlanVersions,
} from "@/hooks/use-plan-governance";
import { formatDateYMD } from "@/lib/format";
import { startMonthOptions } from "@/lib/month-options";
import { formatPlanDelta, formatPlanHours, formatPlanMoney } from "@/lib/plan-format";
import { planStatusLabel } from "@/lib/plan-status-labels";
import { changeOrderAllowed } from "@/lib/resource-plan-lifecycle";
import { cn } from "@/lib/utils";
import type {
  PlanVersionListItemView,
  PlanVersionSummaryView,
  ResourcePlanDetailView,
} from "@/schemas/resource-plans";

const KIND_TONE: Record<string, string> = {
  Sent: "bg-info-100 text-info-700 dark:text-info-400",
  Approved: "bg-success-100 text-success-700 dark:text-success-400",
  Won: "bg-brand-100 text-brand-700 dark:text-brand-300",
  Committed: "bg-muted text-foreground",
};

const MARGIN_TONE: Record<string, string> = {
  green: "text-success-700 dark:text-success-400",
  amber: "text-warning-700 dark:text-warning-400",
  red: "text-error-700 dark:text-error-400",
};

/** `live:<planId>` / `version:<versionId>` — the same ref syntax the API takes. */
type CompareRef = string;

export function PlanVersionsPanel({ plan }: { plan: ResourcePlanDetailView }) {
  const { data: versions, isLoading, isError, error, refetch } = usePlanVersions(plan.id);
  const { data: changeOrderRefs } = useChangeOrderCompareRefs(plan.id);
  const clone = useClonePlan(plan.id);
  const remove = useDeletePlanVersion();
  const [restoreTarget, setRestoreTarget] = useState<PlanVersionListItemView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanVersionListItemView | null>(null);

  const [left, setLeft] = useState<CompareRef | null>(null);
  const [right, setRight] = useState<CompareRef | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not load this plan's versions."}
        onRetry={refetch}
      />
    );
  }

  const rows = versions ?? [];
  const liveRef: CompareRef = `live:${plan.id}`;
  const afterWon = plan.canEditAfterWon === true;
  const canRestore = plan.canRestoreVersion ?? plan.canEdit;
  const hasUnsaved = plan.hasUnsavedChanges ?? plan.uncommittedChangesAt != null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-sm font-semibold">Versions</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Save the plan as a new version any time; it is also saved automatically when the plan is
            sent, approved or won. A saved version never changes or reprices, and restoring one
            makes it the current version again. This plan is at{" "}
            <span className="font-medium text-foreground">version {plan.versionNumber ?? 1}</span>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {changeOrderRefs != null && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setLeft(changeOrderRefs.left);
                setRight(changeOrderRefs.right);
              }}
            >
              <GitCompare className="mr-1.5 h-3.5 w-3.5" />
              Compare with {changeOrderRefs.sourcePlanName}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setCloneOpen((open) => !open)}>
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            Clone
          </Button>
          <CommitVersionButton plan={plan} />
        </div>
      </div>

      {cloneOpen && (
        <CloneForm
          plan={plan}
          versions={rows}
          pending={clone.isPending}
          onCancel={() => setCloneOpen(false)}
          onClone={(input) => {
            clone.mutate(input, { onSuccess: () => setCloneOpen(false) });
          }}
        />
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
          <History className="mx-auto h-5 w-5 text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">No versions yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Use New version to save the plan as it stands.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Version</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">By</th>
                <th className="px-3 py-2 text-right font-medium">Hours</th>
                <th className="px-3 py-2 text-right font-medium">Revenue</th>
                <th className="px-3 py-2 text-right font-medium">Margin</th>
                <th className="px-3 py-2 font-medium">Pod</th>
                <th className="px-3 py-2 text-right font-medium">Compare</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((version) => {
                const ref: CompareRef = `version:${version.id}`;
                return (
                  <tr key={version.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider",
                            KIND_TONE[version.kind] ?? "bg-muted text-muted-foreground",
                          )}
                        >
                          {version.label}
                        </span>
                        {version.isLive && (
                          <span
                            className="rounded border border-success-200 bg-success-100 px-1.5 py-0.5 text-xs font-medium text-success-700 dark:text-success-400"
                            title="The version this plan currently matches."
                          >
                            Live
                          </span>
                        )}
                        {version.commitLabel !== null && (
                          <span className="text-sm text-foreground">{version.commitLabel}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {planStatusLabel(version.planStatusAtFreeze)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {formatDateYMD(version.frozenAt)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {version.frozenByName ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatPlanHours(version.totalHours)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {formatPlanMoney(version.indicativeRevenue)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {version.marginOfRecordPercent === null ? (
                        "—"
                      ) : (
                        <span
                          className={cn(
                            version.marginStatus === null ? "" : MARGIN_TONE[version.marginStatus],
                          )}
                          title={`Measured against the ${version.marginFloorPercent}% floor in force when this version was frozen.`}
                        >
                          {version.marginOfRecordPercent}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {version.shapeSource === null
                        ? "—"
                        : `${version.shapeSource}${version.shapeName === null ? "" : ` · ${version.shapeName}`}`}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          type="button"
                          onClick={() => setLeft(ref)}
                          className={cn(
                            "rounded border px-1.5 py-0.5 text-xs font-medium transition-colors",
                            left === ref
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-muted-foreground hover:text-foreground",
                          )}
                        >
                          A
                        </button>
                        <button
                          type="button"
                          onClick={() => setRight(ref)}
                          className={cn(
                            "rounded border px-1.5 py-0.5 text-xs font-medium transition-colors",
                            right === ref
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-muted-foreground hover:text-foreground",
                          )}
                        >
                          B
                        </button>
                        {/* The live version is restorable only when there is something to
                            throw away — that is "discard my unsaved changes". */}
                        {canRestore && (!version.isLive || hasUnsaved) && (
                          <button
                            type="button"
                            title={`Restore this version into the plan. Replaces the ${plan.lineCount} Plan Line${plan.lineCount === 1 ? "" : "s"} in the grid now.`}
                            aria-label={`Restore ${version.label}`}
                            className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                            disabled={clone.isPending}
                            onClick={() => setRestoreTarget(version)}
                          >
                            <RotateCcw className="h-3 w-3" />
                          </button>
                        )}
                        {version.canDelete && (
                          <button
                            type="button"
                            title="Delete this version. Only you can, because you saved it."
                            aria-label={`Delete ${version.label}`}
                            className="rounded border border-border px-1.5 py-0.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
                            disabled={remove.isPending}
                            onClick={() => setDeleteTarget(version)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>Pick A and B above, or compare against the plan as it stands now:</span>
          <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setRight(liveRef)}>
            Set B to live
          </Button>
          {(left !== null || right !== null) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2"
              onClick={() => {
                setLeft(null);
                setRight(null);
              }}
            >
              Clear
            </Button>
          )}
        </div>
      )}

      <ComparePanel left={left} right={right} />

      <ConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => !open && setRestoreTarget(null)}
        title={`Restore ${restoreTarget?.label ?? "this version"}?`}
        description={restoreDescription(plan, restoreTarget, afterWon)}
        confirmLabel="Restore"
        variant="destructive"
        loading={clone.isPending}
        onConfirm={() => {
          if (restoreTarget === null) return;
          clone.mutate(
            { target: "version", versionId: restoreTarget.id },
            { onSettled: () => setRestoreTarget(null) },
          );
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.label ?? "this version"}?`}
        description="The version and its snapshot are removed for everyone. The audit trail keeps a note that it existed. This can't be undone."
        confirmLabel="Delete version"
        variant="destructive"
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget === null) return;
          remove.mutate(deleteTarget, { onSettled: () => setDeleteTarget(null) });
        }}
      />
    </div>
  );
}

/** What a restore will do, said before it happens — it replaces the grid. */
function restoreDescription(
  plan: ResourcePlanDetailView,
  version: PlanVersionListItemView | null,
  afterWon: boolean,
): string {
  if (version === null) return "";
  const replaced = `${plan.lineCount} Plan Line${plan.lineCount === 1 ? "" : "s"}`;
  // Before Won the price travels with the version; from Won on it is fixed and stays.
  const price = afterWon
    ? " Price, rates and the Contracted Fee are not changed."
    : " The Contracted Fee, discount and contingency come back too.";
  const lost =
    (plan.hasUnsavedChanges ?? plan.uncommittedChangesAt != null)
      ? " Your unsaved changes will be lost and can't be recovered."
      : "";
  return `This replaces the ${replaced} currently in the grid with the ${version.lineCount} from ${version.label} and makes it the current version. No new version is added.${price}${lost}`;
}

/**
 * RP-8.2's clone form.
 *
 * "into a new Draft plan or new version — planner chooses" is a real fork with different
 * consequences, so the two are separate affordances rather than a dropdown: the restore
 * lives on each version's row (where the thing being restored is unambiguous) and this form
 * only ever makes a new plan.
 */
function CloneForm({
  plan,
  versions,
  pending,
  onCancel,
  onClone,
}: {
  plan: ResourcePlanDetailView;
  versions: readonly PlanVersionSummaryView[];
  pending: boolean;
  onCancel: () => void;
  onClone: (input: {
    target: "plan";
    name?: string;
    kind?: string;
    startMonth?: string;
    changeOrderReference?: string | null;
    fromVersionId?: string | null;
  }) => void;
}) {
  const [name, setName] = useState(`${plan.name} (copy)`);
  const [kind, setKind] = useState("Option");
  const [startMonth, setStartMonth] = useState(plan.startMonth);
  const [reference, setReference] = useState("");
  const [fromVersionId, setFromVersionId] = useState("");

  // RP-8.4: a Change Order is raised against a WON plan. Offering it on a Draft would put
  // a button there for the server to refuse.
  const changeOrderAvailable = changeOrderAllowed(plan);

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
      <p className="text-sm leading-relaxed text-muted-foreground">
        Carries the Workstreams, Plan Lines, hours and Planned Pod — not the Contracted Fee or any
        Suggested Resource.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="clone-name" className="text-sm">
            Plan name
          </Label>
          <Input
            id="clone-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="clone-kind" className="text-sm">
            Kind
          </Label>
          <select
            id="clone-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="Base">Base</option>
            <option value="Option">Option</option>
            <option value="ChangeOrder" disabled={!changeOrderAvailable}>
              Change Order{changeOrderAvailable ? "" : " — needs a Won plan"}
            </option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="clone-start" className="text-sm">
            Start month
          </Label>
          <MonthSelect
            id="clone-start"
            size="sm"
            value={startMonth}
            options={startMonthOptions(startMonth)}
            onValueChange={setStartMonth}
          />
          <p className="text-xs text-muted-foreground">Hours move with it.</p>
        </div>
        {kind === "ChangeOrder" && (
          <div className="space-y-1">
            <Label htmlFor="clone-ref" className="text-sm">
              SOW / CO reference
            </Label>
            <Input
              id="clone-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="CO-1"
              className="h-8 text-sm"
            />
          </div>
        )}
        {versions.length > 0 && (
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="clone-from" className="text-sm">
              Copy from
            </Label>
            <select
              id="clone-from"
              value={fromVersionId}
              onChange={(e) => setFromVersionId(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">The plan as it stands now</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label} — frozen {formatDateYMD(v.frozenAt)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              A renewal usually starts from the last agreed shape.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={pending || name.trim() === ""}
          onClick={() =>
            onClone({
              target: "plan",
              name: name.trim(),
              kind,
              startMonth,
              changeOrderReference:
                kind === "ChangeOrder" && reference.trim() !== "" ? reference.trim() : null,
              fromVersionId: fromVersionId === "" ? null : fromVersionId,
            })
          }
        >
          Create the clone
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * RP-8.3's compare. Aligned by Workstream × Labor Category, with deltas.
 *
 * Carries no cost figure at any grain, and needs no `canViewCost` branch — see the header
 * of src/lib/plan-compare.ts.
 */
function ComparePanel({ left, right }: { left: string | null; right: string | null }) {
  const { data, isLoading, isError, error } = usePlanComparison(left, right);

  if (left === null || right === null) return null;
  if (left === right) {
    return (
      <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
        Pick two different versions to compare.
      </p>
    );
  }
  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (isError || data === undefined) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Could not compare these two."}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-display text-sm font-semibold">
          {data.left.label} <span className="text-muted-foreground">vs</span> {data.right.label}
        </h3>
        <span className="font-mono text-sm text-muted-foreground">{data.summary}</span>
      </div>

      <div className="grid gap-2 sm:grid-cols-4">
        <DeltaTile label="Hours" value={data.deltas.hours} />
        <DeltaTile label="Revenue" value={data.deltas.revenue} money />
        <DeltaTile label="Margin" value={data.deltas.marginPoints} suffix=" pts" />
        <DeltaTile label="Blended Rate" value={data.deltas.blendedRate} money />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2 font-medium">Workstream</th>
              <th className="px-3 py-2 font-medium">Labor Category</th>
              <th className="px-3 py-2 text-right font-medium">A hours</th>
              <th className="px-3 py-2 text-right font-medium">B hours</th>
              <th className="px-3 py-2 text-right font-medium">Δ hours</th>
              <th className="px-3 py-2 text-right font-medium">Δ revenue</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr
                key={`${row.workstreamName}/${row.laborCategoryName}`}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-3 py-1.5 text-muted-foreground">{row.workstreamName}</td>
                <td className="px-3 py-1.5">
                  {row.laborCategoryName}
                  {row.presence !== "both" && (
                    <span
                      className={cn(
                        "ml-1.5 rounded px-1 py-0.5 text-xs font-semibold uppercase",
                        row.presence === "rightOnly"
                          ? "bg-success-100 text-success-700 dark:text-success-400"
                          : "bg-error-100 text-error-700 dark:text-error-400",
                      )}
                    >
                      {row.presence === "rightOnly" ? "added" : "dropped"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatPlanHours(row.leftHours)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {formatPlanHours(row.rightHours)}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  <Signed value={row.deltaHours} />
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  <Signed value={row.deltaRevenue} money />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeltaTile({
  label,
  value,
  money,
  suffix,
}: {
  label: string;
  value: string | null;
  money?: boolean;
  suffix?: string;
}) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-mono text-sm font-semibold">
        {value === null ? "—" : <Signed value={value} money={money} suffix={suffix} />}
      </p>
    </div>
  );
}

/** A signed figure, colored by direction and always carrying its sign. */
function Signed({ value, money, suffix }: { value: string; money?: boolean; suffix?: string }) {
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span
      className={
        numeric > 0
          ? "text-success-700 dark:text-success-400"
          : "text-error-700 dark:text-error-400"
      }
    >
      {formatPlanDelta(value, money === true ? "money" : suffix === undefined ? "hours" : "points")}
    </span>
  );
}
